import { useArenaStore } from '../../store/arena-store';
import { useAuthStore } from '../../store/auth-store';
import { PAGINA_CLASSIFICA } from '../../constants/arena';
import ArenaStandings from './ArenaStandings';
import { useArenaPartita } from './useArenaPartita';
/*
 * `Arena.css` e non solo il contenitore: parte delle regole di
 * `ArenaStandings` vive la' — la fascia «+N altri giocatori», lo stato vuoto,
 * le righe di spiegazione — perche' il componente lo rendono piu' pagine e il
 * foglio deve arrivare ovunque arrivi lui. Chi lo monta se lo porta dietro,
 * invece di contare sull'ospite: `ArenaGame` lo importa gia', ma «il mio host
 * ce l'ha» e' una garanzia che il prossimo host non rinnova.
 * `arena-css-tokens.test.ts` lo pretende, e aveva ragione: senza questa riga la
 * fascia e lo stato vuoto arrivavano senza stile.
 */
import './Arena.css';
import '../shared/classifica-in-partita.css';

/**
 * La classifica dell'arena vista DA DENTRO una partita.
 *
 * Gemella di `ClassificaInPartita` (torneo), e per le stesse ragioni: e' la
 * stessa `ArenaStandings` della pagina dell'arena, con i dati che lo store
 * tiene gia' aggiornati durante il gioco — chi gioca resta nella stanza socket
 * dell'arena, e la classifica esce al massimo ogni due secondi. Niente
 * chiamate in piu', niente server.
 *
 * Legge lo store da se' invece di ricevere le righe come prop: cosi' chi la
 * monta (la colonna laterale, che e' `memo`, e il pannello a tutto schermo) le
 * passa un elemento che non cambia mai, e il ridisegno che l'orologio provoca
 * in `ArenaGame` piu' volte al secondo non arriva fin qui.
 *
 * Senza l'occhio «guarda la partita» (`onSpectatePlayer` assente): mentre si
 * gioca non si guarda la partita di un altro, e il server lo rifiuterebbe
 * comunque (`BUSY` in `game/spectator.handler.ts`). E' la stessa scelta della
 * gemella — ed e' anche la garanzia su cui appoggia la nota di
 * `occhioConcesso` in `ArenaStandings`, che prima poteva dire «chi e' in
 * partita non passa di qui» e adesso non piu'.
 */
export default function ClassificaArenaInPartita() {
  /*
   * QUALE classifica: quella dell'arena in cui si GIOCA.
   *
   * `standings` e' filtrata sull'arena APERTA, che non e' per forza quella in
   * cui si gioca (in coda in A si puo' aprire B dall'elenco: vedi
   * `useArenaPartita`). Quando le due divergono la classifica giusta sta in
   * `standingsPartita`.
   *
   * Il ripiego e' `standings`, e il campo nuovo si usa SOLO quando si sa
   * positivamente che le due arene divergono: scritta al rovescio, la
   * condizione con `arenaPartitaId` nullo finirebbe su un array vuoto pur
   * avendo in mano la classifica giusta. `standings` e' anche l'unico dei due
   * fuso con `fondiClassifica` e paginato, quindi e' giusto che sia il caso di
   * riposo.
   *
   * Gli `id` si selezionano come STRINGHE e non prendendo `currentArena`: un
   * selettore che restituisce l'oggetto ridisegna a ogni classifica che
   * arriva, e in arena ne arriva una ogni due secondi.
   */
  const idArenaGuardata = useArenaStore((s) => s.currentArena?.id ?? null);
  const idArenaPartita = useArenaStore((s) => s.arenaPartitaId);
  const divergono = idArenaPartita != null && idArenaGuardata !== idArenaPartita;

  const standings = useArenaStore((s) => s.standings);
  const standingsPartita = useArenaStore((s) => s.standingsPartita);
  const righeInClassifica = useArenaStore((s) => s.righeInClassifica);
  const isLoadingStandings = useArenaStore((s) => s.isLoadingStandings);
  const loadMoreStandings = useArenaStore((s) => s.loadMoreStandings);
  const excludedOpponents = useArenaStore((s) => s.excludedOpponents);

  const userId = useAuthStore((s) => s.user?.id);

  /*
   * Lo stato dell'arena in cui si GIOCA, non di quella aperta: se scade mentre
   * si finisce la partita, le pastiglie «In gioco»/«In pausa» devono spegnersi
   * — sono cose che riguardano ADESSO. E' lo stesso `isActive` della pagina
   * del dettaglio, letto sull'arena giusta.
   */
  const arenaPartita = useArenaPartita();
  const isActive = arenaPartita?.status === 'active';

  const righe = divergono ? standingsPartita : standings;

  return (
    <div className="classifica-in-partita classifica-in-partita--arena" data-testid="classifica-arena-in-partita">
      <ArenaStandings
        standings={righe}
        userId={userId}
        isActive={isActive}
        /* Chi non mi verra' proposto adesso: lo store lo tiene gia' per
           l'arena a cui si e' iscritti, che e' quella in cui si gioca (vedi
           `setExcludedOpponents`). */
        excluded={excludedOpponents}
        /* Le posizioni vere sono le prime venti, quelle del broadcast: sotto,
           la coda la rinumera lo store contando le righe che le stanno sopra.
           Vale in tutt'e due i casi — anche `standingsPartita` e' una pagina
           di broadcast. */
        righeDalServer={PAGINA_CLASSIFICA}
        /*
         * «Mostra altri» solo sull'arena APERTA: `loadMoreStandings` chiede le
         * pagine di `currentArena`, e sull'arena sbagliata appenderebbe a
         * questa classifica righe di un'altra. Dove manca, la fascia non si
         * disegna affatto (vedi `codaDaOffrire`) e restano le venti righe del
         * broadcast piu' la ricerca per nome — con il limite, dichiarato: chi
         * sta sotto la ventesima posizione non trova la propria riga, e il suo
         * posto resta leggibile nell'intestazione.
         */
        righeInClassifica={divergono ? undefined : righeInClassifica}
        onLoadMore={divergono ? undefined : loadMoreStandings}
        isLoadingMore={isLoadingStandings}
      />
    </div>
  );
}
